import {
  parseAuthTokenClaims,
  renewedClaimsUsable,
  shouldRenewToken,
  tokenRegisterBody,
  type AuthTokenClaims,
} from '@visious/core';
import { registerRenewedToken, unregisterOwnSession } from '../api/auth';
import { getPlatform } from './instance';
import { onSessionChange, refreshSession } from './session';

/**
 * The renewal loop's platform half (2026-08-02): when the stored grant carries the opt-in
 * renewal scope and is inside its last week, mint a successor and retire the predecessor. The
 * decisions are core's (`auth/renew.ts`, with the measured §8e facts); what lives here is the
 * credential handling that R9 keeps out of core.
 *
 * Driven by the session store rather than a timer, like the store's own expiry reading: a renewal
 * chance is evaluated on boot and whenever the credential changes — a token inside the window gets
 * exactly one attempt per page load, and a failed attempt retries on the next boot, which for a
 * seven-day window is plenty.
 *
 * The order is the part that must not be reordered: **register → validate → store → revoke the
 * predecessor under its own bearer.** Storing before revoking means a crash anywhere in between
 * leaves a working credential in the store at every step; revoking before storing would open a
 * moment where the only stored token is a dead one. The predecessor's self-revoke is best-effort —
 * unrevoked, it merely ages out — and must carry the *old* bearer explicitly, because the store
 * already holds the successor and unregister is measured to be self-only.
 */

let attempted = false;

export function initTokenRenewal(): void {
  onSessionChange((session) => {
    if (!session.ready || !session.facts.signedIn || !session.facts.canSelfRenew) return;
    void maybeRenew();
  });
}

async function maybeRenew(): Promise<void> {
  if (attempted) return;

  const boot = await getPlatform();
  if (!boot.ok) return;
  const stored = boot.platform.tokens.read();
  if (stored === null) return;

  const nowEpochSec = Math.floor(boot.platform.clock.nowEpochMs() / 1000);
  const claims = claimsOf(stored.token);
  if (claims === null || !shouldRenewToken(claims, nowEpochSec)) return;
  attempted = true;

  const minted = await registerRenewedToken(boot.platform, tokenRegisterBody(claims, nowEpochSec));
  if (!minted.ok) return;

  // The two measured `200`-shaped failures: a successor already expired (the verbatim-`expire`
  // trap) and a successor whose scopes were silently stripped. Neither may replace a live token.
  const successorClaims = claimsOf(minted.value);
  if (successorClaims === null || !renewedClaimsUsable(successorClaims, nowEpochSec)) return;

  const predecessorSession = sessionIdOf(stored.token);
  boot.platform.tokens.write({
    token: minted.value,
    username: stored.username,
    grantedAtEpochSec: nowEpochSec,
  });
  await refreshSession();

  if (predecessorSession !== null) {
    void unregisterOwnSession(boot.platform, stored.token, predecessorSession);
  }
}

function claimsOf(token: string): AuthTokenClaims | null {
  try {
    const parsed = parseAuthTokenClaims(JSON.parse(token));
    return parsed.ok ? parsed.value : null;
  } catch {
    return null;
  }
}

/** The predecessor's own session id — read here because the credential never enters core (R9). */
function sessionIdOf(token: string): string | null {
  try {
    const parsed: unknown = JSON.parse(token);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const session = (parsed as Record<string, unknown>)['session'];
    return typeof session === 'string' && session !== '' ? session : null;
  } catch {
    return null;
  }
}
